import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import { Logo } from 'src/components/Logo';
import { SEO } from 'src/components/SEO';
import { ROUTE_SOURCES } from 'src/constants/routeSources';

type LandingLanguage = 'en' | 'it';

const copyByLanguage = {
  en: {
    seoTitle: 'Thank you | Kipify',
    seoDescription:
      'Thank you for requesting your live audit. Our team will contact you shortly if you match the criteria.',
    title: 'Thank you, request received.',
    subtitle:
      'Our team will review your profile and contact you to schedule the live audit if spots are available.',
    cta: 'Back to landing page',
  },
  it: {
    seoTitle: 'Grazie | Kipify',
    seoDescription:
      'Grazie per aver richiesto il tuo audit live. Il nostro team ti ricontattera presto se rientri nei criteri.',
    title: 'Grazie, richiesta ricevuta.',
    subtitle:
      'Il nostro team valutera il tuo profilo e ti ricontattera per fissare l’audit live se ci sono posti disponibili.',
    cta: 'Torna alla landing',
  },
} as const;

const Page = styled(Box)(() => ({
  minHeight: '100vh',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
  boxSizing: 'border-box',
  color: '#fff',
  background:
    'radial-gradient(circle at 10% 0%, rgba(30, 237, 159, 0.16), transparent 25%), radial-gradient(circle at 100% 10%, rgba(1, 128, 148, 0.18), transparent 30%), linear-gradient(180deg, #071017 0%, #0b0e13 34%, #080c10 100%)',
}));

const Panel = styled(Box)(() => ({
  width: '100%',
  maxWidth: 720,
  borderRadius: 24,
  padding: 32,
  border: '1px solid rgba(255,255,255,0.12)',
  background:
    'linear-gradient(145deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))',
  boxShadow: '0 24px 80px rgba(0, 0, 0, 0.34)',
  backdropFilter: 'blur(18px)',
  textAlign: 'center',
}));

export const LandingAdvThankYou: React.FC = () => {
  const navigate = useNavigate();
  const { lang } = useParams<{ lang?: string }>();
  const routeLanguage: LandingLanguage = lang === 'it' ? 'it' : 'en';
  const copy = copyByLanguage[routeLanguage];

  React.useEffect(() => {
    const htmlLanguage = routeLanguage === 'it' ? 'it' : 'en';
    document.documentElement.lang = htmlLanguage;

    const ensureMetaTag = (name: string) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', name);
        document.head.appendChild(tag);
      }
      return tag;
    };

    const robotsTag = ensureMetaTag('robots');
    const googlebotTag = ensureMetaTag('googlebot');
    const previousRobots = robotsTag.getAttribute('content');
    const previousGooglebot = googlebotTag.getAttribute('content');

    robotsTag.setAttribute('content', 'noindex, nofollow');
    googlebotTag.setAttribute('content', 'noindex, nofollow');

    return () => {
      if (previousRobots) {
        robotsTag.setAttribute('content', previousRobots);
      } else {
        robotsTag.remove();
      }

      if (previousGooglebot) {
        googlebotTag.setAttribute('content', previousGooglebot);
      } else {
        googlebotTag.remove();
      }
    };
  }, [routeLanguage]);

  return (
    <Page>
      <SEO
        title={copy.seoTitle}
        description={copy.seoDescription}
        canonical={`https://app.kipify.io/${ROUTE_SOURCES.LANDING_ADV_THANK_YOU}/${routeLanguage}`}
        ogLocale={routeLanguage === 'it' ? 'it_IT' : 'en_US'}
      />
      <Panel>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Logo variant="white" size="large" />
        </Box>
        <Typography
          component="h1"
          sx={{
            fontSize: { xs: 34, md: 52 },
            fontWeight: 900,
            mb: 2,
            color: '#1EED9F',
          }}
        >
          {copy.title}
        </Typography>
        <Typography
          sx={{
            color: '#dce6f0',
            fontSize: { xs: 16, md: 20 },
            lineHeight: 1.65,
            mb: 4,
          }}
        >
          {copy.subtitle}
        </Typography>
        <Button
          variant="contained"
          onClick={() =>
            navigate(`/${ROUTE_SOURCES.LANDING_ADV}/${routeLanguage}`)
          }
          sx={{
            borderRadius: 999,
            px: 3,
            py: 1.5,
            textTransform: 'none',
            fontWeight: 700,
            backgroundColor: '#1EED9F',
            color: '#071017',
            '&:hover': { backgroundColor: '#17c986' },
          }}
        >
          {copy.cta}
        </Button>
      </Panel>
    </Page>
  );
};
